import { useState } from 'react';

import { ExternalLink, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { useClassifiedCalls } from '@/hooks/useAnalytics';
import { cn } from '@/lib/utils';
import { formatDateTime, formatDuration } from '@/utils/format';

import { CLASSIFICATION_LABELS, OUTCOME_COLORS } from '../constants';

import type { GlobalFilters } from '@/lib/api/analytics.api';
import type { CallClassification } from '@repo/shared-types';

const PAGE_SIZE = 15;

interface CallsPanelProps {
  classification: CallClassification | null;
  excludeClassifications?: string[];
  /** v2: metric key used to resolve the predicate via MetricResolver when
   *  ANALYTICS_V2_ENABLED is on. Takes precedence over `classification`. */
  metricKey?: string;
  excludeMetricKeys?: string[];
  globalFilters: GlobalFilters;
  onClose: () => void;
  /** Override label instead of looking up CLASSIFICATION_LABELS */
  classificationLabel?: string;
}

export function CallsPanel({
  classification,
  excludeClassifications,
  metricKey,
  excludeMetricKeys,
  globalFilters,
  onClose,
  classificationLabel,
}: CallsPanelProps) {
  const [page, setPage] = useState(1);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [prevClassification, setPrevClassification] = useState(classification);
  const isOpen = classification !== null;

  // Reset filters when classification changes (derived state, avoids setState-in-effect)
  if (prevClassification !== classification) {
    setPrevClassification(classification);
    setMinRating(null);
    setPage(1);
  }

  const { data, isLoading, isFetching } = useClassifiedCalls(
    globalFilters,
    classification ?? 'all',
    page,
    PAGE_SIZE,
    { enabled: isOpen },
    minRating ?? undefined,
    excludeClassifications as CallClassification[] | undefined,
    metricKey,
    excludeMetricKeys,
  );

  const calls = data?.data ?? [];
  const pagination = data?.pagination;
  const label = classification
    ? (classificationLabel ?? CLASSIFICATION_LABELS[classification] ?? classification)
    : '';
  // Contact mode lists one row per contact per day; rows open the contact view.
  const contactMode = globalFilters.mode === 'contacts';
  const unitLabel = contactMode ? 'contact-day' : 'call';

  return (
    /* Fixed slide-in panel — no overlay so the canvas stays fully interactive */
    <div
      className={cn(
        'bg-background fixed top-0 right-0 z-50 flex h-full w-full flex-col border-l shadow-2xl transition-transform duration-300 ease-in-out md:w-[860px]',
        isOpen ? 'translate-x-0' : 'translate-x-full',
      )}
      aria-hidden={!isOpen}
    >
      {/* Header */}
      <div className="flex shrink-0 items-start justify-between border-b px-6 py-4">
        <div>
          <h2 className="text-foreground flex items-center gap-2 text-base font-semibold">
            {label}
            {pagination && (
              <span className="text-muted-foreground text-sm font-normal">
                ({pagination.total.toLocaleString()} {unitLabel}
                {pagination.total !== 1 ? 's' : ''})
              </span>
            )}
          </h2>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {contactMode
              ? 'One row per contact per day (their latest matching call). Rows open the contact.'
              : 'Click any row to open the full call detail.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:bg-muted hover:text-foreground ml-4 shrink-0 rounded-lg p-1.5 transition-colors"
          aria-label="Close panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Rating filter bar */}
      <div className="flex shrink-0 items-center gap-1 border-b px-6 py-2">
        <span className="text-muted-foreground mr-1 text-xs">Min score:</span>
        {([null, 4, 5, 6, 7, 8, 9] as const).map((val) => (
          <button
            key={val ?? 'any'}
            type="button"
            onClick={() => {
              setMinRating(val);
              setPage(1);
            }}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              minRating === val
                ? val === null
                  ? 'bg-muted text-foreground'
                  : val >= 8
                    ? 'bg-amber-500/25 text-amber-300'
                    : 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            {val === null ? 'Any' : `≥${val}`}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="min-h-0 flex-1 overflow-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
          </div>
        ) : calls.length === 0 ? (
          <div className="flex h-40 items-center justify-center">
            <p className="text-muted-foreground text-sm">No calls match this classification.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  #
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Customer
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Phone
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Outcome
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Dir
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Duration
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Intent
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Score
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium">
                  Date
                </th>
                <th className="text-muted-foreground px-4 py-2.5 text-left text-xs font-medium" />
              </tr>
            </thead>
            <tbody className={cn('divide-border divide-y', isFetching && 'opacity-60')}>
              {calls.map((call, idx) => (
                <tr key={call.id} className="hover:bg-muted/50 transition-colors">
                  <td className="text-muted-foreground px-4 py-2.5 text-xs">
                    {(page - 1) * PAGE_SIZE + idx + 1}
                  </td>
                  <td className="text-foreground px-4 py-2.5 font-medium">
                    {call.customer_name ?? (
                      <span className="text-muted-foreground italic">Unknown</span>
                    )}
                  </td>
                  <td className="text-muted-foreground px-4 py-2.5 text-xs tabular-nums">
                    {call.phone_number ?? '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-semibold',
                        call.outcome && OUTCOME_COLORS[call.outcome]
                          ? OUTCOME_COLORS[call.outcome]
                          : 'bg-slate-100 text-slate-600',
                      )}
                    >
                      {call.outcome ? call.outcome.replace(/_/g, ' ') : 'PENDING'}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={cn(
                        'rounded px-1.5 py-0.5 text-[10px] font-medium',
                        call.direction === 'INBOUND'
                          ? 'bg-blue-500/15 text-blue-400'
                          : 'bg-violet-500/15 text-violet-400',
                      )}
                    >
                      {call.direction === 'INBOUND' ? '\u2193' : '\u2191'} {call.direction}
                    </span>
                  </td>
                  <td className="text-muted-foreground px-4 py-2.5 tabular-nums">
                    {formatDuration(call.duration)}
                  </td>
                  <td className="px-4 py-2.5">
                    {call.buying_intent_level !== null ? (
                      <span className="text-foreground font-semibold">
                        {call.buying_intent_level}/5
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {call.rating_score !== null ? (
                      <span className="font-semibold text-amber-400">{call.rating_score}/10</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="text-muted-foreground px-4 py-2.5 text-xs tabular-nums">
                    {formatDateTime(call.executed_at)}
                  </td>
                  <td className="px-4 py-2.5">
                    <Link
                      to={
                        contactMode && call.contact_id
                          ? `/sales-console/${call.contact_id}`
                          : `/calls/${call.id}`
                      }
                      title={contactMode && call.contact_id ? 'Open contact' : 'Open call'}
                      className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="shrink-0 border-t px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
